import {
  filter,
  merge,
  type Observable,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs"
import type { CfiManager } from "../cfi"
import type { Context } from "../context/Context"
import type { Spine } from "../spine/Spine"
import type { SpineItemsManager } from "../spine/SpineItemsManager"
import type { SpinePosition, UnboundSpinePosition } from "../spine/types"
import type { createSpineItemLocator } from "../spineItem/locationResolver"
import type { SpineItem } from "../spineItem/SpineItem"
import { DestroyableClass } from "../utils/DestroyableClass"
import { waitForSwitch } from "../utils/rxjs"
import type { Pagination } from "./Pagination"

export class PaginationController extends DestroyableClass {
  constructor(
    protected context: Context,
    protected pagination: Pagination,
    protected spineItemsManager: SpineItemsManager,
    protected spine: Spine,
    protected spineItemLocator: ReturnType<typeof createSpineItemLocator>,
    protected isNavigationLocked$: Observable<boolean>,
    protected cfi: CfiManager,
  ) {
    super()

    /**
     * Adjust heavier pagination once the navigation and items are updated.
     * This is also cancelled if the layout changes, because the layout will
     * trigger a new navigation adjustment and pagination again.
     *
     * This adjustment is used to update the pagination with the most up to date values we can.
     * It needs to be ran only when viewport is free because some operation such as looking up cfi can
     * be really heavy.
     *
     * The cfi will only be updated if it needs to be:
     * - cfi is a root target
     * - cfi is undefined
     * - items are different
     */
    const updatePagination$ = merge(
      this.context.bridgeEvent.navigation$,
      spine.layout$,
    ).pipe(
      switchMap(() => {
        const getVisiblePagesFromViewportPosition = ({
          spineItem,
          position,
        }: {
          spineItem: SpineItem
          position: SpinePosition | UnboundSpinePosition
        }) =>
          this.spine.locator.getVisiblePagesFromViewportPosition({
            spineItem: spineItem,
            position,
            threshold: { type: "percentage", value: 0.5 },
          })

        /**
         * @important
         *
         * It's important to soft update pagination immediately.
         * This will avoid delay in potential user feedbacks (navigation buttons).
         *
         * However we wait for the navigator to be unlocked, this avoid updating the pagination
         * while the user is panning for example. We consider a locked navigator as unfinished
         * navigation.
         *
         * Nothing here should be heavier than layout lookup.
         */
        return this.isNavigationLocked$.pipe(
          filter((isLocked) => !isLocked),
          take(1),
          withLatestFrom(this.context.bridgeEvent.navigation$),
          tap(([, navigation]) => {
            const { position } = navigation
            const previousPagination = this.pagination.value

            const {
              beginIndex: beginSpineItemIndex,
              endIndex: endSpineItemIndex,
            } =
              this.spine.locator.getVisibleSpineItemsFromPosition({
                position,
                threshold: { type: "percentage", value: 0.5 },
              }) ?? {}

            const beginSpineItem =
              this.spineItemsManager.get(beginSpineItemIndex)
            const endSpineItem = this.spineItemsManager.get(endSpineItemIndex)

            if (!beginSpineItem || !endSpineItem) return

            const beginLastCfi = previousPagination.beginCfi
            const endLastCfi = previousPagination.endCfi

            const { beginPageIndex = 0 } =
              getVisiblePagesFromViewportPosition({
                spineItem: beginSpineItem,
                position,
              }) ?? {}

            const { endPageIndex = 0 } =
              getVisiblePagesFromViewportPosition({
                spineItem: endSpineItem,
                position,
              }) ?? {}

            const shouldUpdateBeginCfi =
              beginLastCfi === undefined ||
              this.cfi.isRootCfi(beginLastCfi) ||
              previousPagination.beginSpineItemIndex !== beginSpineItemIndex

            const shouldUpdateEndCfi =
              previousPagination.endSpineItemIndex !== endSpineItemIndex ||
              endLastCfi === undefined ||
              this.cfi.isRootCfi(endLastCfi)

            const beginCfi = shouldUpdateBeginCfi
              ? this.cfi.generateRootCfi(beginSpineItem.item)
              : beginLastCfi

            const endCfi = shouldUpdateEndCfi
              ? this.cfi.generateRootCfi(endSpineItem.item)
              : endLastCfi

            const beginNumberOfPagesInSpineItem = beginSpineItem.numberOfPages

            const endNumberOfPagesInSpineItem = endSpineItem.numberOfPages

            this.pagination.update({
              beginCfi,
              beginNumberOfPagesInSpineItem,
              beginPageIndexInSpineItem: beginPageIndex,
              beginSpineItemIndex,
              endCfi,
              endNumberOfPagesInSpineItem,
              endPageIndexInSpineItem: endPageIndex,
              endSpineItemIndex,
              navigationId: navigation.id,
            })
          }),
        )
      }),
    )

    /**
     * Heavy operation, needs to be optimized as much as possible.
     *
     * @todo add more optimization, comparing item before, after with position, etc
     */
    const updateCfi$ = updatePagination$.pipe(
      waitForSwitch(this.context.bridgeEvent.viewportFree$),
      tap(() => {
        const {
          beginSpineItemIndex,
          endSpineItemIndex,
          beginPageIndexInSpineItem,
          endPageIndexInSpineItem,
        } = this.pagination.value

        if (
          beginPageIndexInSpineItem === undefined ||
          endPageIndexInSpineItem === undefined ||
          beginSpineItemIndex === undefined ||
          endSpineItemIndex === undefined
        )
          return

        const beginSpineItem = this.spineItemsManager.get(beginSpineItemIndex)
        const endSpineItem = this.spineItemsManager.get(endSpineItemIndex)

        if (beginSpineItem === undefined || endSpineItem === undefined) return

        const beginPageEntry = this.spine.pages.fromSpineItemPageIndex(
          beginSpineItem,
          beginPageIndexInSpineItem,
        )
        const endPageEntry = this.spine.pages.fromSpineItemPageIndex(
          endSpineItem,
          endPageIndexInSpineItem,
        )

        // @todo only update long cfi if the item layout change but specifically its content
        this.pagination.update({
          beginCfi: beginPageEntry?.firstVisibleNode
            ? this.cfi.generateCfiForSpineItemPage({
                spineItem: beginSpineItem.item,
                pageNode: beginPageEntry?.firstVisibleNode,
              })
            : this.cfi.generateRootCfi(beginSpineItem.item),
          endCfi: endPageEntry?.firstVisibleNode
            ? this.cfi.generateCfiForSpineItemPage({
                spineItem: endSpineItem.item,
                pageNode: endPageEntry?.firstVisibleNode,
              })
            : this.cfi.generateRootCfi(endSpineItem.item),
        })
      }),
    )

    merge(updatePagination$, updateCfi$)
      .pipe(takeUntil(this.destroy$))
      .subscribe()
  }
}
